import { Component, Input, ElementRef, ViewChild, AfterViewInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FaqItem } from '../../models/faq.model';

@Component({
  selector: 'app-faq',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './faq.component.html',
  styleUrl: './faq.component.scss'
})
export class FaqComponent implements AfterViewInit, OnDestroy {
  @Input() faqList: FaqItem[] = [];

  activeIndex: number | null = null;
  isVisible = false; // Controls the 'start-animation' class

  @ViewChild('faqSection') faqSection!: ElementRef;
  @ViewChild('tiltContainer') tiltContainer!: ElementRef;

  private observer: IntersectionObserver | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) { }

  toggleAccordion(index: number): void {
    if (this.activeIndex === index) {
      this.activeIndex = null;
    } else {
      this.activeIndex = index;
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupScrollAnimation();
      this.setupTiltEffect();
    }
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // 1. Scroll Trigger Logic
  private setupScrollAnimation(): void {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Triggers the CSS class .start-animation
          this.isVisible = true;
          
          // Stop observing once triggered
          if (this.observer) this.observer.disconnect();
        }
      });
    }, {
      threshold: 0.2 // Animation starts when 20% of section is visible
    });

    if (this.faqSection) {
      this.observer.observe(this.faqSection.nativeElement);
    }
  }

  // 2. 3D Tilt Logic
  private setupTiltEffect(): void {
    const container = this.tiltContainer.nativeElement;

    // Target the card for the 3D transform
    const card = container.querySelector('.image-card') as HTMLElement;

    if (!card) return;

    const mouseMoveHandler = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const xc = rect.width / 2;
      const yc = rect.height / 2;

      const maxRotate = 15; // Max rotation degrees

      const rotateY = ((x - xc) / xc) * maxRotate;
      const rotateX = ((yc - y) / yc) * maxRotate; // Inverse Y axis

      // Apply 3D transform instantly (no transition delay in CSS for this property)
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.05, 1.05, 1.05)`;
    };

    const mouseLeaveHandler = () => {
      // Reset position with a smooth transition (handled in JS or CSS transition)
      card.style.transition = 'transform 0.5s ease';
      card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    };

    const mouseEnterHandler = () => {
      // Remove transition for instant mouse following
      card.style.transition = 'none';
    };

    container.addEventListener('mousemove', mouseMoveHandler);
    container.addEventListener('mouseleave', mouseLeaveHandler);
    container.addEventListener('mouseenter', mouseEnterHandler);
  }
}